import { useEffect, useState, useRef, useCallback } from 'react';
import type { IUseCountdownTimerProps } from 'types/propTypes';

export function useCountdownTimer({ initialSeconds = 45, autoStart = false }: IUseCountdownTimerProps = {}) {
  const [secondsLeft, setSecondsLeft] = useState(initialSeconds);
  const [isRunning, setIsRunning] = useState(autoStart);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const stop = useCallback(() => {
    setIsRunning(false);
    clearTimer();
  }, [clearTimer]);

  const start = useCallback(() => {
    stop();
    setSecondsLeft(initialSeconds);
    setIsRunning(true);
  }, [initialSeconds, stop]);

  const reset = useCallback(() => {
    setSecondsLeft(initialSeconds);
  }, [initialSeconds]);

  useEffect(() => {
    if (!isRunning || secondsLeft <= 0) {
      stop();
      return undefined;
    }

    timerRef.current = setInterval(() => {
      setSecondsLeft((prev) => Math.max(prev - 1, 0));
    }, 1000);

    return clearTimer;
  }, [isRunning, secondsLeft, stop, clearTimer]);

  const formattedTime = `${Math.floor(secondsLeft / 60)
    .toString()
    .padStart(2, '0')}:${(secondsLeft % 60).toString().padStart(2, '0')}`;

  return {
    secondsLeft,
    formattedTime,
    isRunning,
    start,
    reset,
    stop,
  };
}
